import env from '../services/AppEnv';
import { cloneDeep } from 'lodash';
import { isGranted } from "../services/AccessManager";
import log from "../services/Logger";
import trans from "../services/Translator";
import formUtils from "../components/form/FormUtils";
import alerts from "../services/Alerts";
import capabilities from "../services/Capabilities";
const checkPermission = (to, from, next) => {
    const permission = routePermission(to.matched);
    if (permission && !isGranted(permission)) {
        alerts.error(trans('app.security_error'), trans('app.security_error.access_denied'));
        next('');
        return;
    }
    next();
};
/**
 * The permission a route needs, taken from the nearest matched record that names one, so that a
 * parent's `meta.permission` covers its descendants. Exported for the navigation search, which
 * must not offer a route the guard would refuse.
 */
export function routePermission(matched) {
    const nearestWithPermission = matched.slice()
        .reverse()
        .find(r => r.meta && r.meta.permission);
    return nearestWithPermission
        ? nearestWithPermission.meta['permission']
        : undefined;
}
/**
 * The capability a route needs, taken from the nearest matched record that names one, so that a
 * parent's `meta.capability` covers its descendants. Exported because the navigation asks the same
 * question about the route a nav item points at, and the two must not answer it differently.
 */
export function routeCapability(matched) {
    const nearestWithCapability = matched.slice()
        .reverse()
        .find(r => r.meta && r.meta.capability);
    return nearestWithCapability
        ? nearestWithCapability.meta['capability']
        : undefined;
}
/**
 * Keeps a route out of reach when no application provides the capability serving it, so that a
 * bookmarked or redirected-to URL cannot render a view with nothing behind it. Sends the user to
 * the default route rather than raising the access-denied alert of checkPermission: the feature is
 * not deployed, which is not the same as not being allowed.
 */
const checkCapability = (to, from, next) => {
    const capability = routeCapability(to.matched);
    if (capability && !capabilities.has(capability)) {
        log(`No application provides '${capability}', skipping ${String(to.name)}`);
        next('');
        return;
    }
    next();
};
const checkDirtyForms = (to, from, next) => {
    if (!formUtils.confirmDirtyForms()) {
        next(false);
        return;
    }
    next();
};
const updatePageTitle = (to, from, next) => {
    try {
        const nearestWithTitle = to.matched.slice()
            .reverse()
            .find(r => r.meta && r.meta.title);
        setPageTitle(nearestWithTitle ? trans(nearestWithTitle.meta['title']) : null);
    }
    catch (e) {
        log(e);
    }
    next();
};
const setPageTitle = (title) => {
    const defaultTitle = env.app.title;
    document.title = `${title || ''}${title ? ' - ' : ''}${defaultTitle}`;
};
function singleParam(route, key) {
    const val = route.params[key];
    return Array.isArray(val) ? val[0] : val;
}
export function resolveRouteParams(_params, item) {
    const params = cloneDeep(_params);
    Object.keys(params).forEach(param => {
        if (params[param].indexOf('{') === 0) {
            const prop = params[param].substring(1, params[param].length - 1);
            if (item[prop]) {
                params[param] = item[prop];
            }
            else {
                delete params[param];
            }
        }
    });
    return params;
}
export default {
    updatePageTitle: updatePageTitle,
    setPageTitle: setPageTitle,
    checkPermission: checkPermission,
    checkCapability: checkCapability,
    checkDirtyForms: checkDirtyForms,
    singleParam: singleParam,
    resolveRouteParams: resolveRouteParams,
};
